import { type ComponentProps } from 'react'
import { useWebchat } from '../hooks'
import { Chat } from './Chat'
import type { UserCredentials } from '@botpress/webchat-client'
import { LatestWebchatConfig } from '@bpinternal/shared'

type Props = {
  clientId: string
  apiUrl?: string
  storageKey?: string
  user?: UserCredentials
  conversationId?: string
  configuration?: LatestWebchatConfig
  soundOn?: boolean
  toggleSound?: () => void
} & Pick<ComponentProps<typeof Chat>, 'allowFileUpload' | 'renderers'> &
  ComponentProps<'div'>

export const Webchat = ({
  clientId,
  storageKey,
  configuration,
  allowFileUpload,
  apiUrl,
  user,
  conversationId,
  ...props
}: Props) => {
  const offlineStorageKey = storageKey || `bp-webchat-${clientId}`
  const {
    clientState,
    client,
    isTyping,
    newConversation,
    messages,
    participants,
    user: webchatUser,
    error,
    soundOn,
    toggleSound,
  } = useWebchat({
    clientId,
    apiUrl,
    conversationId,
    user,
    storageKey: offlineStorageKey,
    storageLocation: configuration?.storageLocation,
    adminSecret: configuration?.adminSecret,
    soundEnabled: configuration?.soundEnabled,
  })

  const { sendMessage, uploadFile, addMessageFeedback } = client ?? {}

  return (
    <Chat
      configuration={configuration ?? {}}
      user={webchatUser?.userId ? { id: webchatUser?.userId } : undefined}
      isLoading={clientState === 'connecting'}
      isTyping={isTyping}
      connected={clientState === 'connected' ? true : clientState === 'disconnected' ? false : undefined}
      messages={messages}
      sendMessage={sendMessage}
      uploadFile={uploadFile}
      addMessageFeedback={addMessageFeedback}
      allowFileUpload={allowFileUpload}
      restartConversation={newConversation}
      {...props}
      participants={participants}
      error={error}
      clientId={clientId}
      soundOn={soundOn}
      toggleSound={toggleSound}
    />
  )
}
